import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useStrapiConfig } from "hooks/useStrapiConfig";
import { getLogoUrl } from "theme";

interface PageMetaProps {
  name: string;
}

const FAVICON_SIZE = 64;

// Browser tabs force favicons into a square. The tenant logo is often a
// wide/rectangular image, so using it directly as the favicon href makes it
// look stretched. Instead, draw it "contain"-fit and centered onto a square
// canvas and use that as the favicon.
function buildSquareFavicon(imageUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = FAVICON_SIZE;
      canvas.height = FAVICON_SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }
      const scale = Math.min(FAVICON_SIZE / image.width, FAVICON_SIZE / image.height);
      const width = image.width * scale;
      const height = image.height * scale;
      const x = (FAVICON_SIZE - width) / 2;
      const y = (FAVICON_SIZE - height) / 2;
      ctx.drawImage(image, x, y, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => resolve(null);
    image.src = imageUrl;
  });
}

// Sets the browser tab title and favicon for a page: title is the given
// page name, favicon is the tenant's configured logo (same source Layout/
// Header use for the on-page logo).
const PageMeta = ({ name }: PageMetaProps): JSX.Element => {
  const { data: loginPageConfig } = useStrapiConfig();
  const logoUrl = getLogoUrl(loginPageConfig?.login_page_style);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logoUrl) {
      setFaviconUrl(null);
      return;
    }
    let cancelled = false;
    buildSquareFavicon(logoUrl).then((dataUrl) => {
      if (!cancelled) setFaviconUrl(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  return (
    <Helmet>
      <title>{name}</title>
      {faviconUrl && <link rel="icon" href={faviconUrl} />}
    </Helmet>
  );
};

export default PageMeta;
